import { db } from "../lib/firebase";
import {
  collection,
  getDocs,
  query,
  where,
  Timestamp,
} from "firebase/firestore";

export type AnalyticsRange = "7d" | "30d" | "90d" | "all";

export interface CourseAnalytics {
  id: string;
  title: string;
  thumbnail: string;
  price: number;
  totalStudents: number;
  totalRevenue: number;
  completionRate: number;
  totalLessons: number;
}

export interface InstructorAnalyticsSummary {
  totalCourses: number;
  totalStudents: number;
  totalRevenue: number;
  avgCompletionRate: number;
  topCoursesByRevenue: Array<{ courseId: string; title: string; revenue: number }>;
  topCoursesByStudents: Array<{ courseId: string; title: string; students: number }>;
}

export interface RevenuePoint {
  date: string;
  revenue: number;
}

export interface CompletionPoint {
  date: string;
  completionRate: number;
}

export interface TopCourseSalesPoint {
  name: string;
  revenue: number;
  sales: number;
}

const getRangeStart = (range: AnalyticsRange): Date | null => {
  if (range === "all") return null;
  const days = range === "7d" ? 7 : range === "30d" ? 30 : 90;
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d;
};

const chunk = <T,>(arr: T[], size: number): T[][] => {
  const out: T[][] = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
};

const pad2 = (n: number) => String(n).padStart(2, "0");

const formatDayKey = (d: Date) => {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
};

const formatMonthKey = (d: Date) => {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}`;
};

const buildTimeAxis = (range: AnalyticsRange, granularity: "day" | "month") => {
  const start = getRangeStart(range);
  const end = new Date();

  if (!start) return [] as string[];

  const keys: string[] = [];
  const cursor = new Date(start);
  cursor.setHours(0, 0, 0, 0);
  end.setHours(0, 0, 0, 0);

  if (granularity === "day") {
    while (cursor <= end) {
      keys.push(formatDayKey(cursor));
      cursor.setDate(cursor.getDate() + 1);
    }
  } else {
    cursor.setDate(1);
    while (cursor <= end) {
      keys.push(formatMonthKey(cursor));
      cursor.setMonth(cursor.getMonth() + 1);
    }
  }

  return keys;
};

export const loadInstructorAnalytics = async (
  instructorId: string,
  range: AnalyticsRange
): Promise<{
  summary: InstructorAnalyticsSummary;
  courseStats: CourseAnalytics[];
  revenueSeries: RevenuePoint[];
  completionSeries: CompletionPoint[];
  topCourseSales: TopCourseSalesPoint[];
}> => {
  // Courses
  const coursesQ = query(
    collection(db, "courses"),
    where("instructorId", "==", instructorId)
  );
  const coursesSnap = await getDocs(coursesQ);
  const courses = coursesSnap.docs.map((d) => ({ id: d.id, ...(d.data() as any) }));

  const rangeStart = getRangeStart(range);
  const rangeStartTs = rangeStart ? Timestamp.fromDate(rangeStart) : null;

  const revenueGranularity: "day" | "month" = range === "all" ? "month" : "day";
  const completionGranularity: "day" | "month" = range === "all" ? "month" : "day";
  const revenueAxis = range === "all" ? [] : buildTimeAxis(range, revenueGranularity);
  const completionAxis = range === "all" ? [] : buildTimeAxis(range, completionGranularity);
  const revenueMap = new Map<string, number>();
  const completionNumeratorMap = new Map<string, number>();
  const completionDenominatorMap = new Map<string, number>();

  const courseIds: string[] = courses.map((c) => c.id);

  // Prefetch lessons count per course (batched by courseId)
  const lessonsCountByCourseId = new Map<string, number>();
  for (const ids of chunk(courseIds, 10)) {
    const lessonsQ = query(collection(db, "lessons"), where("courseId", "in", ids));
    const lessonsSnap = await getDocs(lessonsQ);
    lessonsSnap.docs.forEach((d) => {
      const data: any = d.data();
      const cid = data.courseId;
      if (!cid) return;
      lessonsCountByCourseId.set(cid, (lessonsCountByCourseId.get(cid) || 0) + 1);
    });
  }

  // Prefetch payments and progress (batched by courseIds)
  const paymentsByCourseId = new Map<string, Array<any>>();
  const progressByCourseId = new Map<string, Array<any>>();

  for (const ids of chunk(courseIds, 10)) {
    const paymentsQ = query(
      collection(db, "payments"),
      where("courseId", "in", ids),
      where("status", "==", "completed")
    );
    const paymentsSnap = await getDocs(paymentsQ);
    paymentsSnap.docs.forEach((d) => {
      const data: any = d.data();
      const cid = data.courseId;
      if (!cid) return;
      // Only count instructor-approved payments
      if (!data.approvedBy) return;
      if (rangeStartTs && data.createdAt?.toMillis) {
        if (data.createdAt.toMillis() < rangeStartTs.toMillis()) return;
      }
      const arr = paymentsByCourseId.get(cid) || [];
      arr.push(data);
      paymentsByCourseId.set(cid, arr);
    });

    const progressQ = query(
      collection(db, "progress"),
      where("courseId", "in", ids),
      where("completed", "==", true)
    );
    const progressSnap = await getDocs(progressQ);
    progressSnap.docs.forEach((d) => {
      const data: any = d.data();
      const cid = data.courseId;
      if (!cid) return;
      if (rangeStartTs && data.completedAt?.toMillis) {
        if (data.completedAt.toMillis() < rangeStartTs.toMillis()) return;
      }
      const arr = progressByCourseId.get(cid) || [];
      arr.push(data);
      progressByCourseId.set(cid, arr);
    });
  }

  let totalStudents = 0;
  let totalRevenue = 0;
  const courseStats: CourseAnalytics[] = [];

  for (const course of courses) {
    const payments = paymentsByCourseId.get(course.id) || [];
    const progresses = progressByCourseId.get(course.id) || [];

    const buyerSet = new Set<string>();
    let courseRevenue = 0;
    payments.forEach((data) => {
      if (data.userId) buyerSet.add(data.userId);
      courseRevenue += Number(data.amount || 0);
      if (data.createdAt?.toDate) {
        const d = data.createdAt.toDate() as Date;
        const key = revenueGranularity === "month" ? formatMonthKey(d) : formatDayKey(d);
        revenueMap.set(key, (revenueMap.get(key) || 0) + Number(data.amount || 0));
      }
    });

    const courseStudents = buyerSet.size;
    const totalLessons = lessonsCountByCourseId.get(course.id) || 0;

    // Completion trend
    progresses.forEach((data) => {
      if (!data.completedAt?.toDate) return;
      const d = data.completedAt.toDate() as Date;
      const key = completionGranularity === "month" ? formatMonthKey(d) : formatDayKey(d);
      completionNumeratorMap.set(key, (completionNumeratorMap.get(key) || 0) + 1);
    });

    // Denominator per bucket: students * lessons
    if (courseStudents > 0 && totalLessons > 0) {
      const axis = range === "all" ? [] : completionAxis;
      if (axis.length > 0) {
        axis.forEach((key) => {
          completionDenominatorMap.set(
            key,
            (completionDenominatorMap.get(key) || 0) + courseStudents * totalLessons
          );
        });
      }
    }

    const denom = courseStudents * totalLessons;
    const completionRate = denom > 0 ? Math.round((progresses.length / denom) * 100) : 0;

    courseStats.push({
      id: course.id,
      title: course.title || "",
      thumbnail: course.thumbnail || "",
      price: Number(course.price || 0),
      totalStudents: courseStudents,
      totalRevenue: courseRevenue,
      completionRate: isNaN(completionRate) ? 0 : Math.min(100, Math.max(0, completionRate)),
      totalLessons,
    });

    totalStudents += courseStudents;
    totalRevenue += courseRevenue;
  }

  const avgCompletionRate =
    courseStats.length > 0
      ? Math.round(courseStats.reduce((sum, c) => sum + c.completionRate, 0) / courseStats.length)
      : 0;

  const topCoursesByRevenue = [...courseStats]
    .sort((a, b) => b.totalRevenue - a.totalRevenue)
    .slice(0, 5)
    .map((c) => ({ courseId: c.id, title: c.title, revenue: c.totalRevenue }));

  const topCoursesByStudents = [...courseStats]
    .sort((a, b) => b.totalStudents - a.totalStudents)
    .slice(0, 5)
    .map((c) => ({ courseId: c.id, title: c.title, students: c.totalStudents }));

  const summary: InstructorAnalyticsSummary = {
    totalCourses: courses.length,
    totalStudents,
    totalRevenue,
    avgCompletionRate,
    topCoursesByRevenue,
    topCoursesByStudents,
  };

  // Revenue series
  let revenueSeries: RevenuePoint[] = [];
  if (range === "all") {
    // Use months present in revenueMap
    const keys = [...revenueMap.keys()].sort();
    revenueSeries = keys.map((k) => ({ date: k, revenue: revenueMap.get(k) || 0 }));
  } else {
    revenueSeries = revenueAxis.map((k) => ({ date: k, revenue: revenueMap.get(k) || 0 }));
  }

  // Completion series
  let completionSeries: CompletionPoint[] = [];
  if (range === "all") {
    const keys = [...completionNumeratorMap.keys()].sort();
    completionSeries = keys.map((k) => {
      const numerator = completionNumeratorMap.get(k) || 0;
      const denom = completionDenominatorMap.get(k) || 0;
      const rate = denom > 0 ? Math.round((numerator / denom) * 100) : 0;
      return { date: k, completionRate: Math.min(100, Math.max(0, rate)) };
    });
  } else {
    completionSeries = completionAxis.map((k) => {
      const numerator = completionNumeratorMap.get(k) || 0;
      const denom = completionDenominatorMap.get(k) || 0;
      const rate = denom > 0 ? Math.round((numerator / denom) * 100) : 0;
      return { date: k, completionRate: Math.min(100, Math.max(0, rate)) };
    });
  }

  // Top course sales
  const topCourseSales: TopCourseSalesPoint[] = [...courseStats]
    .sort((a, b) => b.totalRevenue - a.totalRevenue)
    .slice(0, 7)
    .map((c) => ({
      name: c.title,
      revenue: c.totalRevenue,
      sales: c.price > 0 ? Math.round(c.totalRevenue / c.price) : 0,
    }));

  return { summary, courseStats, revenueSeries, completionSeries, topCourseSales };
};
